import { axiosClient, AxiosRequestConfig } from 'utils/gql/axios/AxiosClient';
import { MediaTypeModel } from '@utmost.co/enterprise-models';

const getData = axiosClient.get;
const postData = axiosClient.post;
const deleteData = axiosClient.delete;

export type ServicePrefix = '' | '/gwp' | '/rv';

function withDefaultConfig(config: AxiosRequestConfig = {}, prefix: ServicePrefix = ''): AxiosRequestConfig {
  const prefixType = prefix === '' ? 'oc' : prefix.replaceAll('/', '');
  return {
    ...config,
    headers: {
      accept: `application/vnd.salteese.${prefixType}.v1.0+json`,
      'content-type': `application/vnd.salteese.${prefixType}.v1.0+json`,
      ...config.headers
    }
  };
}

function getUrl(url: string, prefix: ServicePrefix = '') {
  const cleanedUrl = url.indexOf('/') === 0 ? url.substring(1) : url;
  return `${prefix}/api/${cleanedUrl}`;
}

export function get<T>(url: string, config: AxiosRequestConfig = {}, prefix: ServicePrefix = '') {
  return getData<T>(getUrl(url), withDefaultConfig(config, prefix));
}

export function post<T>(url: string, data: any, config: AxiosRequestConfig = {}, prefix: ServicePrefix = '') {
  const targetConfig = withDefaultConfig(config, prefix);
  return postData<T>(getUrl(url), data, {
    ...targetConfig,
    headers: {
      ...targetConfig.headers,
      'X-UTMOST-ORIGIN-SERVICE': 'OC_UI' // needed for audit log entries
    }
  });
}

export function del<T>(url: string, data: any, config: AxiosRequestConfig = {}, prefix: ServicePrefix = '') {
  return deleteData<T>(getUrl(url), withDefaultConfig(config, prefix));
}

export function getFileData(file: File) {
  return new Promise<ArrayBuffer>((resolve) => {
    const reader = new FileReader();
    reader.onload = () => {
      resolve(reader.result as ArrayBuffer);
    };
    reader.readAsArrayBuffer(file);
  });
}

export function downloadAsFile(data: string, filename: string, mime: string) {
  // It is necessary to create a new blob object with mime-type explicitly set
  // otherwise only Chrome works like it should
  const blob = new Blob([data], { type: mime || 'application/octet-stream' });
  if (typeof (window.navigator as any).msSaveBlob !== 'undefined') {
    // IE doesn't allow using a blob object directly as link href.
    // Workaround for "HTML7007: One or more blob URLs were
    // revoked by closing the blob for which they were created.
    // These URLs will no longer resolve as the data backing
    // the URL has been freed."
    (window.navigator as any).msSaveBlob(blob, filename);
    return;
  }
  // Other browsers
  // Create a link pointing to the ObjectURL containing the blob
  const blobURL = window.URL.createObjectURL(blob);
  const tempLink = document.createElement('a');
  tempLink.style.display = 'none';
  tempLink.href = blobURL;
  tempLink.setAttribute('download', filename);
  // Safari thinks _blank anchor are pop ups. We only want to set _blank
  // target if the browser does not support the HTML5 download attribute.
  // This allows you to download files in desktop safari if pop up blocking
  // is enabled.
  if (typeof tempLink.download === 'undefined') {
    tempLink.setAttribute('target', '_blank');
  }
  document.body.appendChild(tempLink);
  tempLink.click();
  document.body.removeChild(tempLink);
  setTimeout(() => {
    // For Firefox it is necessary to delay revoking the ObjectURL
    window.URL.revokeObjectURL(blobURL);
  }, 100);
}

export async function axiosUploadMediaToOc<T>(
  url: string,
  file: File,
  mediaType: MediaTypeModel,
  onUploadProgress: (progressEvent: any) => void = () => {},
  extraParams?: Map<string, string>,
  signal?: AbortSignal
) {
  const bytes = await getFileData(file);
  return axiosUploadBlobToOc<T>(
    url,
    new Blob([new Uint8Array(bytes)], { type: 'application/octet-stream' }),
    mediaType,
    file.name,
    onUploadProgress,
    extraParams,
    signal
  );
}

export async function axiosUploadBlobToOc<T>(
  url: string,
  blob: Blob,
  mediaType: MediaTypeModel,
  filename: string,
  onUploadProgress: (progressEvent: any) => void = () => {},
  extraParams?: Map<string, string>,
  signal?: AbortSignal
) {
  const formData = new FormData();
  formData.set('file', blob);
  formData.set('filename', filename);
  formData.set('mediaType', mediaType);

  if (extraParams && extraParams.size) {
    extraParams.forEach((value, key) => {
      formData.set(key, value);
    });
  }

  const config: AxiosRequestConfig = {
    headers: { 'content-type': 'multipart/form-data' },
    onUploadProgress,
    signal
  };

  return post<T>(url, formData, config);
}
